import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DateRangeValue } from "../components/ui/dateRangeFilter/dateRangeFilter.component";

/**
 * Reads/writes start_date & end_date as URL query params, same pattern as the
 * "status" quick filter in jobsListActions.component.tsx. This lets a toolbar
 * component and its content page (siblings, not parent/child) share the same
 * date-range state purely through the URL.
 */
export const useDateRangeQueryParams = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const dateRange: DateRangeValue = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      start_date: params.get("start_date"),
      end_date: params.get("end_date"),
    };
  }, [location.search]);

  const setDateRange = useCallback(
    (value: DateRangeValue) => {
      const nextParams = new URLSearchParams(location.search);

      if (value.start_date) {
        nextParams.set("start_date", value.start_date);
      } else {
        nextParams.delete("start_date");
      }

      if (value.end_date) {
        nextParams.set("end_date", value.end_date);
      } else {
        nextParams.delete("end_date");
      }

      const nextSearch = nextParams.toString();
      navigate({
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      });
    },
    [location.pathname, location.search, navigate]
  );

  return { dateRange, setDateRange };
};
